/**
 * PAGE.ACTIONS
 */

import {
  Page,
  PAGE_ACTIVATE,
  PAGE_ADD,
  PAGE_EDIT,
  PAGE_INIT,
  PAGE_GET,
  PAGE_REMOVE,
  PAGE_STATUS,
  PAGE_REPLACE,
  PAGE_CONTENT_TAGS,
  PageContentTag,
  PAGE_CHANGE,
  PAGE_SIDES,
  PAGE_HEATMAP_LIMIT
} from "./page.types"
import { v4 as uuid } from "uuid"
import { store } from "@/index"
import { EditorState, RawDraftContentBlock, convertFromRaw } from "draft-js"
import { fetchAttributes, fetchMessages, fetchNote, fetchParticipation, fetchQuestions } from "./_archive.actions"
import { DashboardFilters } from "./filter.types"
import { Survey } from "./survey.types"
import { surveyFormatNote } from "./survey.actions"
import { populationToFilterLite } from "@/utils/filters-to-attributes.utils"
import { Population } from "./population.types"
import { rpsQuestionsGroupForReport, topicGroupForReport } from "./topic.actions"
import { flatMap, flatten, orderBy, sum, uniq, uniqBy } from "lodash"
import getGenerativeAssistantSummary from "@/utils/get-generative-assistant-summary.utils"
import i18n from "@/translate/i18n"
import { Session } from "./_session.types"
import { MESSAGE_CEIL_COUNT_SUMMARY } from "./message.types"
import { questionActivate } from "./question.actions"
import { getRepartitionCeils } from "@/utils/get-repartition-ceils.utils"

/* REDUX */

export const pageActivate = (page: Page) => ({
  type: PAGE_ACTIVATE,
  payload: {
    page
  }
})

export const pageAdd = (page: Page, index: number) => ({
  type: PAGE_ADD,
  payload: {
    page,
    index
  }
})

export const pageChange = (goNext: boolean) => ({
  type: PAGE_CHANGE,
  payload: {
    goNext
  }
})

export const pageEdit = (key: string, value: any) => ({
  type: PAGE_EDIT,
  payload: {
    key,
    value
  }
})

export const pageInit = () => ({
  type: PAGE_INIT
})

export const pageGet = (pages: Page[]) => ({
  type: PAGE_GET,
  payload: {
    pages
  }
})

export const pageRemove = (id: string) => ({
  type: PAGE_REMOVE,
  payload: {
    id
  }
})

export const pageReplace = (page: Page) => ({
  type: PAGE_REPLACE,
  payload: {
    page
  }
})

export const pageStatus = (status: string) => ({
  type: PAGE_STATUS,
  payload: {
    status
  }
})

//API

export const pageFetch = (reportId: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/pages",
    data: {
      reportId
    }
  }
})

export const pageFetch_AsSupervisor = (reportId: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/supervisor/pages",
    data: {
      reportId
    }
  }
})

export const pageDestroy_AsSupervisor = (pageId: string) => ({
  type: "API",
  payload: {
    method: "DELETE",
    url: "/supervisor/page/delete/" + pageId
  }
})

export const pageDestroy = (pageId: string) => ({
  type: "API",
  payload: {
    method: "DELETE",
    url: "/page/delete/" + pageId
  }
})

//Activate and edit page
export const pageActivateAndEdit: any = (page: Page, key: string, value: any) => (dispatch) => {
  dispatch(pageActivate(page))
  dispatch(pageEdit(key, value))
}

//Create page
export function pageCreateNew(data, reportId: string): Page {
  const accountOptions = store.getState()._session.accountOptions
  const newPageId: string = uuid()
  const dateNow: number = Date.now()

  //Init surveyId
  const surveyId = store.getState().survey.active.id
  PAGE_SIDES.forEach((side) => {
    if (data[side]?.screenshot) {
      data[side].screenshot.surveyId = data[side].screenshot.surveyId = surveyId

      //Init heatmap param according admin parameters
      if (data[side]?.screenshot.type === "heatmap") {
        if (store.getState()._session.userRole === "ADMIN") {
          data[side].screenshot.filterName = accountOptions.heatmapFilterName
          data[side].screenshot.resultsType = accountOptions.heatmapResultsType
        }
      }
    }
  })

  //Init page
  const page = new Page(data)
  page.id = newPageId
  page.timestamp = dateNow
  page.ReportId = reportId

  return page
}

//Fetch note
export async function pageFetchNote(
  populations: Population[],
  isAccount: boolean,
  specificSurveyId: string | null,
  isReport: boolean = false
) {
  const dashboardFilters = pageInitDashboardFilters(populations)
  const withSatisfaction = store.getState()._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction"
  const response: any = await store.dispatch(
    fetchNote(
      dashboardFilters,
      withSatisfaction,
      specificSurveyId ? specificSurveyId : undefined,
      undefined,
      isAccount,
      isReport
    )
  )
  return surveyFormatNote(response.note, withSatisfaction)
}

//Fetch participation
export async function pageFetchParticipation(
  populations: Population[],
  isAccount: boolean,
  specificSurveyId: string | null,
  isReport: boolean = false
) {
  const dashboardFilters = pageInitDashboardFilters(populations)
  const response: any = await store.dispatch(
    fetchParticipation(
      dashboardFilters,
      specificSurveyId ? specificSurveyId : undefined,
      undefined,
      isAccount,
      isReport
    )
  )
  const participationRate = new Survey(response).getParticipationRate(
    store.getState()._session.accountOptions.hideStartedForParticipation
  )
  return participationRate.toString()
}

//Fetch population name
//For observer match names of the filters according attributes of the survey
export async function pageFetchPopulations(session: Session, reportPopulations: Population[]) {
  if (session.userRole === "OBSERVER" && !reportPopulations.length) {
    let populations = store.getState().filter.observerAttributes
    const filterNames: any[] = uniq(populations.map((x) => x.filterName))

    let attributes: any[] = []
    for (let i = 0; i < filterNames.length; i++) {
      const filterName = filterNames[i]
      const response: any = await store.dispatch(fetchAttributes(new DashboardFilters(), filterName))
      if (!response.error) {
        attributes = attributes.concat(response)
      }
    }

    populations = populations.filter((x) => attributes.map((x) => x.aid.toString()).indexOf(x.id) > -1)
    return populations
  } else {
    return reportPopulations
  }
}

//Return population as string format
export async function pageFetchPopulationName(session: Session, reportPopulations: Population[]) {
  const populations = await pageFetchPopulations(session, reportPopulations)
  return populations.length ? populations.map((x) => x.name).join(", ") : session.accountName
}

//Format editor state from raw content
//withTag differs if displayed in the editor box or in the preview
export function pageFormatEditorState(textBlocks: RawDraftContentBlock[], withTags: boolean) {
  if (withTags) {
    textBlocks = pageFormatContentWithTags(textBlocks)
  }

  const contentState = convertFromRaw({ blocks: textBlocks, entityMap: {} })
  return EditorState.createWithContent(contentState)
}

//Format all the texts blocks with the tags
export function pageFormatContentWithTags(textBlocks: RawDraftContentBlock[]) {
  const outputBlocks: RawDraftContentBlock[] = []

  textBlocks.forEach((textBlock) => {
    const text = pageReplaceTag(textBlock.text)

    //Compare length between texts
    //Adapt style limit in function
    const diff = text.length - textBlock.text.length
    if (diff > 0 && textBlock.inlineStyleRanges.length) {
      const inlineStyleRanges = Array.from(textBlock.inlineStyleRanges)

      //Detect all last style range (where offset and lenght match lenght of text blocks
      //Update length
      inlineStyleRanges
        .filter((x) => x.offset + x.length === textBlock.text.length)
        .forEach((styleRange) => {
          styleRange.length = styleRange.length + diff
        })

      outputBlocks.push(
        Object.assign({}, textBlock, {
          text,
          inlineStyleRanges
        })
      )
    } else {
      outputBlocks.push(Object.assign({}, textBlock, { text }))
    }
  })

  return outputBlocks
}

//Get specific value for a tag
export function pageGetTagText(tag: PageContentTag) {
  const text = store.getState().report.active.tagsData[tag]
  return text ? text : ""
}

//Get last surveys in order to fetch history data
export function pageGetLastSurvey(index: number) {
  //Get surveys associated to the project and sort them by date
  const surveys = store.getState().survey.list.filter((x) => x.ProjectId === store.getState().survey.active.ProjectId)
  surveys.sort((d1, d2) => new Date(d2.dateStart).getTime() - new Date(d1.dateStart).getTime())

  //Return survey
  return surveys[index]
}

//Return id for the last survey
export function pageGetLastSurveyId(index: number) {
  const survey = pageGetLastSurvey(index)
  return survey ? survey.id : null
}

//Get year of the survey
export function pageGetSurveyYear(index: number) {
  if (index === 0) {
    return new Date(store.getState().survey.active.dateStart).getFullYear()
  } else {
    const survey = pageGetLastSurvey(index)
    return survey ? new Date(survey.dateStart).getFullYear() : ""
  }
}

//Update text with matching variables
export function pageReplaceTag(text: string) {
  let modifiedText = String(text)
  const tags = PAGE_CONTENT_TAGS.filter((x) => text.indexOf("{{" + x + "}}") > -1)
  tags.forEach((tag) => {
    const tagText = pageGetTagText(tag)
    if (tagText) {
      modifiedText = modifiedText.replaceAll("{{" + tag + "}}", tagText)
    }
  })

  return modifiedText.length > 0 ? modifiedText : text
}

//Init dashboard filters for the widgets
export function pageInitDashboardFilters(populations: Population[]) {
  if (!populations.length) {
    populations = store.getState().report.active.options.populations
  }

  const dashboardFilters = new DashboardFilters()
  populations.forEach((population) => {
    if (population.aid) {
      population.id = population.aid.toString()
    }
  })

  dashboardFilters.customFilters = populationToFilterLite([], populations)
  return dashboardFilters
}

//Count rows for rps gollac topics per zone
async function countGollacTopicsRows(zone: "exposed" | "safe"): Promise<number> {
  const ALLOWED_SPECIALS = new Set([
    "psychological-job-demands",
    "decision-latitude",
    "skill-use",
    "team-support",
    "manager-support",
    "reconnaissance"
  ])

  const session = store.getState()._session
  const topicState = store.getState().topic
  const ceilThresholdGollac = getRepartitionCeils(session, true, false)[0] * 10

  const data: any = await store.dispatch(fetchQuestions(new DashboardFilters(), null, undefined, undefined, true))

  if (data?.error) return 0

  const rows: any[] = []

  topicState.list.forEach((tp: any) => {
    tp.Questions.forEach((q: any) => {
      if (!ALLOWED_SPECIALS.has(q.special)) return

      const current = data.find((x: any) => x.aid === q.aid)
      if (!current) return

      const repartition = current.repartition ?? []
      const total = sum(repartition)
      if (!total) return

      const currentZone: "exposed" | "safe" = current.note < ceilThresholdGollac ? "exposed" : "safe"

      if (currentZone === zone) {
        rows.push({ question: q, topic: tp, note: current.note })
      }
    })
  })

  return rows.length
}

//Count rows for rps gollac steps (all topics)
async function countGollacStepsRows(): Promise<number> {
  const topicState = store.getState().topic
  return topicState.list.length
}

const REPORT_COMMENTS_MAX_LINES_PER_PAGE = 32
const REPORT_COMMENTS_CHARS_PER_LINE = 95
const REPORT_COMMENTS_HEADER_LINES = 3

function estimateReportCommentLines(text: string) {
  return Math.max(1, Math.ceil(text.length / REPORT_COMMENTS_CHARS_PER_LINE))
}

function splitReportCommentText(text: string, maxLines: number) {
  const normalizedText = (text || "").replace(/\s+/g, " ").trim()
  const maxChars = Math.max(40, maxLines * REPORT_COMMENTS_CHARS_PER_LINE)

  if (!normalizedText) return [""]
  if (normalizedText.length <= maxChars) return [normalizedText]

  const chunks: string[] = []
  const words = normalizedText.split(" ")
  let currentChunk = ""

  words.forEach((word) => {
    if (!currentChunk) {
      currentChunk = word
      return
    }

    if (`${currentChunk} ${word}`.length <= maxChars) {
      currentChunk = `${currentChunk} ${word}`
      return
    }

    chunks.push(currentChunk)
    currentChunk = word
  })

  if (currentChunk) chunks.push(currentChunk)

  return chunks
}

function pageCreateTextBlock(text: string, index: number, styles: string[] = ["fontsize-8"]): RawDraftContentBlock {
  const inlineStyleRanges: any[] = text.length
    ? styles.map((style) => ({
        offset: 0,
        length: text.length,
        style
      }))
    : []

  return {
    key: `block${index}`,
    text,
    type: "unstyled",
    depth: 0,
    inlineStyleRanges,
    entityRanges: [],
    data: {}
  }
}

function pageCreateCommentsBlock(comments: { label: string; text: string }[], index: number): RawDraftContentBlock {
  const blockText = comments.map((comment) => `${comment.label}\n${comment.text}`).join("\n")
  const inlineStyleRanges: any[] = blockText.length
    ? [
        {
          offset: 0,
          length: blockText.length,
          style: "fontsize-8"
        }
      ]
    : []

  let cursor = 0
  comments.forEach((comment) => {
    inlineStyleRanges.push({
      offset: cursor,
      length: comment.label.length,
      style: "color-#8F8F8F"
    })
    cursor += `${comment.label}\n${comment.text}\n`.length
  })

  return {
    key: `block${index}`,
    text: blockText,
    type: "unstyled",
    depth: 0,
    inlineStyleRanges,
    entityRanges: [],
    data: {}
  }
}

function pageBuildMessageTextBlocks(title: string, messages: { text?: string }[]) {
  const pages: RawDraftContentBlock[][] = []
  let pageComments: { label: string; text: string }[] = []
  let lineCount = 0
  let blockIndex = 0

  function startPage() {
    pageComments = []
    lineCount = REPORT_COMMENTS_HEADER_LINES
  }

  function pushPage() {
    if (pageComments.length) {
      pages.push([
        pageCreateTextBlock(title, blockIndex++, ["fontsize-8", "BOLD"]),
        pageCreateCommentsBlock(pageComments, blockIndex++)
      ])
    }
    startPage()
  }

  function addCommentChunk(label: string, text: string) {
    const requiredLines = 1 + estimateReportCommentLines(text)

    if (lineCount + requiredLines > REPORT_COMMENTS_MAX_LINES_PER_PAGE && pageComments.length) {
      pushPage()
    }

    pageComments.push({ label, text })
    lineCount += requiredLines
  }

  startPage()

  messages.forEach((message, messageIndex) => {
    const maxCommentLines = REPORT_COMMENTS_MAX_LINES_PER_PAGE - REPORT_COMMENTS_HEADER_LINES - 2
    const chunks = splitReportCommentText(message.text ?? "", maxCommentLines)

    chunks.forEach((chunk, chunkIndex) => {
      const label = `Commentaire ${messageIndex + 1}${chunkIndex ? " (suite)" : ""}`
      addCommentChunk(label, chunk)
    })
  })

  pushPage()

  return pages
}

function getReportTemplateTopics() {
  const templateTopics = store.getState().survey.active.template.Topics

  return templateTopics.length ? templateTopics : store.getState().topic.list
}

function hasNpsQuestion() {
  return getReportTemplateTopics().some((topic) =>
    topic.Questions.some(
      (question) =>
        question.type === "scale" && question.responseCount === 10 && question.special === "nps" && question.withScore
    )
  )
}

function pageHasScreenshotType(page: Page, screenshotType: string) {
  return (
    (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === screenshotType) ||
    (page.contentRight.type === "screenshot" && page.contentRight.screenshot.type === screenshotType)
  )
}

//Init page generation
//For new report create a page for each organisation but do not duplicate pages for topics, axes etc...
export async function pageInitGenerate(inputPages: Page[], initNewReport: boolean) {
  const pages: Page[] = []

  for (let i = 0; i < inputPages.length; i++) {
    const page = inputPages[i]

    if (initNewReport && pageHasScreenshotType(page, "nps") && !hasNpsQuestion()) {
      continue
    }

    if (!initNewReport) {
      //For messages create a page for each message
      if (
        page.contentLeft.type === "screenshot" &&
        page.contentLeft.screenshot.type === "messages" &&
        store.getState()._session.isTabDisplayed("/dashboard/messages")
      ) {
        //Get questions
        const questionsDetails: any = await store.dispatch(
          fetchQuestions(store.getState().filter.dashboard, "open", undefined, undefined, true)
        )

        //Créer une page pour chaque question
        const questionsOpen = uniqBy(
          flatMap(
            getReportTemplateTopics().filter((x) => {
              if (page.contentLeft.screenshot.displayAll) return true
              return page.contentLeft.screenshot.hideItems.indexOf(x.aid ?? "") === -1
            }),
            (x) => x.Questions
          ).filter((x) => x.type === "open"),
          "aid"
        )

        const getQuestionDetails = (questionOpen) => {
          if (questionsDetails.error) return questionOpen
          return questionsDetails.find((x) => x.aid?.toString() === questionOpen.aid?.toString()) ?? questionOpen
        }

        const visibleQuestionsDetails = questionsOpen
          .map((questionOpen) => getQuestionDetails(questionOpen))
          .filter((questionDetails) => questionDetails && questionDetails.messagesCount > 0)
        const hasPossibleSummary = visibleQuestionsDetails.some(
          (questionDetails) => questionDetails.messagesCount >= MESSAGE_CEIL_COUNT_SUMMARY
        )
        //The AI summary only when the summarize module is active for this user (account module, hidden for observers)
        const session = store.getState()._session
        const isSummarizeActive = session.modules.survey.summarize && session.getModuleObserver("summarize")
        const displayMessages =
          page.contentLeft.screenshot.displayMessages ||
          page.contentLeft.screenshot.summarize === false ||
          !hasPossibleSummary ||
          !isSummarizeActive

        if (!hasPossibleSummary) {
          page.contentLeft.screenshot.displayMessages = true
        }

        //Loop on each question
        for (let q = 0; q < questionsOpen.length; q++) {
          const questionOpen = questionsOpen[q]
          const questionDetails = getQuestionDetails(questionOpen)

          if (questionDetails && questionDetails.messagesCount > 0) {
            store.dispatch(questionActivate(questionOpen))

            //Question primary find
            let label = ""
            const questionPrimary = flatten(getReportTemplateTopics().map((x) => x.Questions)).find(
              (x) => x.id === questionOpen.primaryId
            )

            if (questionPrimary) {
              label = questionPrimary.label + " - " + questionOpen.label
            } else {
              label = questionOpen.label
            }

            // MODE: Display all messages (afficher liste brute paginée)
            if (displayMessages) {
              const messages: any = await store.dispatch(
                fetchMessages(
                  store.getState().filter.dashboard,
                  questionOpen.aid,
                  null,
                  undefined,
                  undefined,
                  store.getState()._session.language,
                  true
                )
              )

              if (messages.error) continue

              const title = i18n.t("page_type_screenshot_messages_help", { question: label })
              const textBlocksPages = pageBuildMessageTextBlocks(title, messages)

              textBlocksPages.forEach((textBlocks) => {
                const newPage = new Page(page)
                newPage.twoColumns = false
                newPage.contentLeft.type = "text"
                newPage.contentLeft.initialType = "screenshot"
                newPage.contentLeft.textBlocks = textBlocks
                if (textBlocks.length > 0) pages.push(newPage)
              })
            }
            // MODE: Summarize (résumé IA - comportement par défaut)
            else if (questionDetails.messagesCount >= MESSAGE_CEIL_COUNT_SUMMARY) {
              //Fetch messages
              const messages: any = await store.dispatch(
                fetchMessages(
                  store.getState().filter.dashboard,
                  questionOpen.aid,
                  null,
                  undefined,
                  undefined,
                  undefined,
                  true
                )
              )

              //Custom prompt
              questionOpen.prompt = questionDetails.prompt
              questionOpen.isPromptCustom = questionDetails.isPromptCustom

              //Call open ai
              //If the assistant is unavailable (down, quota exceeded...), fall back to a
              //notice instead of letting the whole report generation hang or crash
              let summary: any
              try {
                summary = await getGenerativeAssistantSummary(
                  questionOpen,
                  label,
                  messages.map((x) => x.text),
                  questionPrimary ? false : true
                )
              } catch {
                summary = i18n.t("assistant_service_unavailable")
              }

              const textBlocks: any[] = pageParseString(
                i18n.t("page_type_screenshot_messages_summarize_help", { question: label }) +
                  "(" +
                  i18n.t("dashboard_messages") +
                  " : " +
                  questionDetails.messagesCount +
                  ")" +
                  "\n\n" +
                  summary
              )

              if (textBlocks.length > 0) {
                const newPage = new Page(page)
                newPage.twoColumns = false
                newPage.contentLeft.type = "text"
                newPage.contentLeft.initialType = "screenshot"
                newPage.contentLeft.textBlocks = textBlocks
                pages.push(newPage)
              }
            }
          }
        }
      }

      //For topics create a page by topics
      else if (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === "topics") {
        store.getState().axis.list.forEach((axis) => {
          const newPage = new Page(page)
          newPage.Topics = store
            .getState()
            .topic.list.filter((x) => x.AxisId === axis.id && page.contentLeft.screenshot.isTopicDisplayed(x) && x.note)
          if (newPage.Topics.length > 0) {
            pages.push(newPage)
          }
        })
      }
      //For question create a page by question
      //For history, display 6 questions by rows
      else if (
        page.contentLeft.type === "screenshot" &&
        (page.contentLeft.screenshot.type === "questions" || page.contentLeft.screenshot.type === "history")
      ) {
        topicGroupForReport(page).forEach((topics) => {
          const newPage = new Page(page)
          newPage.Topics = topics
          pages.push(newPage)
        })
      }
      //For heatmap divide the heatmap by rows
      else if (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === "heatmap") {
        const heatmapPages = Math.ceil(store.getState().report.active.heatmapLength / PAGE_HEATMAP_LIMIT)
        for (let i = 0; i < heatmapPages; i++) {
          const newPage = new Page(page)
          newPage.heatmapOffset = i * PAGE_HEATMAP_LIMIT
          pages.push(newPage)
        }
      }
      //For rps questions full page
      else if (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === "rps_questions") {
        const dashboardFilters = pageInitDashboardFilters(store.getState().report.active.options.populations)

        const data: any = await store.dispatch(fetchQuestions(dashboardFilters, null, undefined, undefined, true))

        let questions: any[] = []

        store.getState().topic.list.forEach((topic) => {
          topic.Questions.forEach((question: any) => {
            const currentQuestion = data.find((x) => x.aid === question.aid && question.special)

            if (currentQuestion) {
              const repartition = currentQuestion.repartition || []
              const specialAnswer = sum(repartition)

              if (specialAnswer) {
                const negativesAnswers = repartition.length > 2 ? repartition[0] + repartition[1] : repartition[0]

                const specialRate = (negativesAnswers / specialAnswer) * 100

                questions.push({
                  ...question,
                  repartition,
                  specialAnswer,
                  negativesAnswers,
                  specialRate
                })
              }
            }
          })
        })

        questions = orderBy(questions, ["specialRate"], ["desc"])
        const groups = rpsQuestionsGroupForReport(page, questions)

        groups.forEach((_, index) => {
          const newPage = new Page(page)

          newPage.rpsQuestionsGroupIndex = index

          pages.push(newPage)
        })
      }
      //For rps gollac topics: 3 items per page, exposed zone first then safe zone
      else if (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === "rps_gollac_topics") {
        const itemsPerPage = 6

        // Count items in exposed zone
        const exposedRowCount = await countGollacTopicsRows("exposed")
        const exposedPageCount = Math.ceil(exposedRowCount / itemsPerPage)

        // Create pages for exposed zone
        for (let i = 0; i < exposedPageCount; i++) {
          const newPage = new Page(page)
          newPage.rpsGollacOffset = i * itemsPerPage
          newPage.rpsGollacZone = "exposed"
          pages.push(newPage)
        }

        // Count items in safe zone
        const safeRowCount = await countGollacTopicsRows("safe")
        const safePageCount = Math.ceil(safeRowCount / itemsPerPage)

        // Create pages for safe zone
        for (let i = 0; i < safePageCount; i++) {
          const newPage = new Page(page)
          newPage.rpsGollacOffset = i * itemsPerPage
          newPage.rpsGollacZone = "safe"
          pages.push(newPage)
        }
      }
      //For rps gollac steps: description on page 1, then table with items per page
      else if (page.contentLeft.type === "screenshot" && page.contentLeft.screenshot.type === "rps_gollac_steps") {
        // Page 1: description only
        const firstPage = new Page(page)
        firstPage.rpsGollacIsFirstPage = true
        pages.push(firstPage)

        // Pages 2+: table with items per page
        const itemsPerPage = 8
        const stepsRowCount = await countGollacStepsRows()
        const stepsPageCount = Math.ceil(stepsRowCount / itemsPerPage)

        for (let i = 0; i < stepsPageCount; i++) {
          const newPage = new Page(page)
          newPage.rpsGollacIsFirstPage = false
          newPage.rpsGollacOffset = i * itemsPerPage
          pages.push(newPage)
        }
      } else {
        pages.push(new Page(page))
      }
    } else {
      pages.push(new Page(page))
    }
  }

  //Add page for each attribute if specified
  const newPages: Page[] = []
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]

    if (page.options.filterName && page.options.filterMode === "all") {
      const attributes: any = await store.dispatch(
        fetchAttributes(store.getState().filter.dashboard, page.options.filterName)
      )
      if (!attributes.error) {
        attributes.forEach((attribute) => {
          const newPage = new Page(page)
          newPage.options.filterMode = "one"
          newPage.options.populations = [
            new Population({
              aid: attribute.aid.toString(),
              id: attribute.filterName + "_" + attribute.name,
              filterName: attribute.filterName,
              name: attribute.name
            })
          ]

          if (initNewReport) {
            newPage.id = uuid()
          }

          newPages.push(newPage)
        })
      }
    } else {
      newPages.push(page)
    }
  }

  return newPages
}

//Parse raw text (from ChatGPT API for exemple)
export function pageParseString(string: string) {
  const lines = string.split("\n").filter((line) => line.trim() !== "")

  return lines.map((line, index) => {
    // Handle bold text
    const boldPattern = /\*\*(.*?)\*\*/g
    const boldMatches = [...line.matchAll(boldPattern)]

    const inlineStyleRanges: any[] = []
    const plainText = line.replace(boldPattern, "$1") // Replace bold markers with the plain text

    // Prepare inline styles for bold
    boldMatches.forEach((match) => {
      const offset = plainText.indexOf(match[1])
      inlineStyleRanges.push({
        offset: offset,
        length: match[1].length,
        style: "BOLD"
      })
    })

    // Identify list items
    let blockType = "unstyled" // Default block type
    if (/^\d+\./.test(line)) {
      blockType = "unordered-list-item"
    }

    //Force fontsize 10
    inlineStyleRanges.push({
      offset: 0,
      length: plainText.length,
      style: "fontsize-8"
    })

    return {
      key: `block${index}`,
      text: plainText,
      type: blockType,
      depth: 0,
      inlineStyleRanges,
      entityRanges: [],
      data: {}
    }
  })
}

//Get specific value for a tag
//Fetch data if required
export async function pageUpdateTagData(tag: PageContentTag, isReport = false) {
  const activeSurvey = store.getState().survey.active
  const session = store.getState()._session
  const reportPopulations = store.getState().report.active.options.populations

  if (session.interfaceType === "SUPERVISOR") {
    return ""
  } else {
    switch (tag) {
      case "account_name":
        return session.accountName

      case "account_note":
        return await pageFetchNote([], true, null, isReport)

      case "account_note_last_1":
        return await pageFetchNote([], true, pageGetLastSurveyId(1), isReport)

      case "account_note_last_2":
        return await pageFetchNote([], true, pageGetLastSurveyId(2), isReport)

      case "account_participation_rate":
        return await pageFetchParticipation([], true, null, isReport)

      case "account_participation_rate_last_1":
        return await pageFetchParticipation([], true, pageGetLastSurveyId(1), isReport)

      case "account_participation_rate_last_2":
        return await pageFetchParticipation([], true, pageGetLastSurveyId(2), isReport)

      case "axes_names":
        return store
          .getState()
          .axis.list.map((x) => "• " + x.label)
          .join("\n")

      case "population_name": {
        return await pageFetchPopulationName(session, reportPopulations)
      }

      case "participation_min":
        return session.participationMin.toString()

      case "survey_date_end": {
        const dateEnd = activeSurvey.dateEnd ? new Date(activeSurvey.dateEnd as Date) : new Date()
        return dateEnd.toLocaleDateString()
      }
      case "survey_date_start":
        return new Date(activeSurvey.dateStart).toLocaleDateString()

      case "survey_name":
        return activeSurvey.name

      case "survey_note":
        return await pageFetchNote(reportPopulations, false, null, isReport)

      case "survey_note_last_1":
        return await pageFetchNote(reportPopulations, false, pageGetLastSurveyId(1), isReport)

      case "survey_note_last_2":
        return await pageFetchNote(reportPopulations, false, pageGetLastSurveyId(2), isReport)

      case "survey_participation_rate":
        return await pageFetchParticipation(reportPopulations, false, null, isReport)

      case "survey_participation_rate_last_1":
        return await pageFetchParticipation(reportPopulations, false, pageGetLastSurveyId(1), isReport)

      case "survey_participation_rate_last_2":
        return await pageFetchParticipation(reportPopulations, false, pageGetLastSurveyId(2), isReport)

      case "survey_year":
        return pageGetSurveyYear(0)

      case "survey_year_last_1":
        return pageGetSurveyYear(1)

      case "survey_year_last_2":
        return pageGetSurveyYear(2)

      default:
        return ""
    }
  }
}
